import { Modal, TextInput } from "@mantine/core";
import { useForm, schemaResolver } from "@mantine/form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faXmark } from "@fortawesome/sharp-regular-svg-icons";
import { z } from "zod";
import { SearchCriteria } from "../../types/search";
import styles from "./search-modal.module.scss";

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSearch: (criteria: SearchCriteria) => void;
  initialCriteria: SearchCriteria;
}

const searchSchema = z
  .object({
    patientFirstName: z.string().optional(),
    patientLastName: z.string().optional(),
    patientDateOfBirth: z.string().optional(),
    patientExternalId: z.string().optional(),
  })
  .refine(
    (data) =>
      (data.patientFirstName && data.patientFirstName.trim().length > 0) ||
      (data.patientLastName && data.patientLastName.trim().length > 0) ||
      (data.patientDateOfBirth && data.patientDateOfBirth.trim().length > 0) ||
      (data.patientExternalId && data.patientExternalId.trim().length > 0),
    {
      message: "At least one search field must be filled",
      path: ["patientFirstName"],
    },
  )
  .refine(
    (data) => {
      if (
        data.patientDateOfBirth &&
        data.patientDateOfBirth.trim().length > 0
      ) {
        const date = new Date(data.patientDateOfBirth);
        return date <= new Date();
      }
      return true;
    },
    {
      message: "Date of birth cannot be in the future",
      path: ["patientDateOfBirth"],
    },
  );

type SearchFormValues = {
  patientFirstName?: string;
  patientLastName?: string;
  patientDateOfBirth?: string;
  patientExternalId?: string;
};

export const SearchModal = ({
  isOpen,
  onClose,
  onSearch,
  initialCriteria,
}: SearchModalProps) => {
  const form = useForm<SearchFormValues>({
    initialValues: {
      patientFirstName: initialCriteria.patientFirstName,
      patientLastName: initialCriteria.patientLastName,
      patientDateOfBirth: initialCriteria.patientDateOfBirth,
      patientExternalId: initialCriteria.patientExternalId,
    },
    validate: schemaResolver(searchSchema, { sync: true }),
  });

  const handleSubmit = (values: SearchFormValues) => {
    const searchCriteria: SearchCriteria = {
      patientFirstName: values.patientFirstName?.trim() || "",
      patientLastName: values.patientLastName?.trim() || "",
      patientDateOfBirth: values.patientDateOfBirth?.trim() || "",
      patientExternalId: values.patientExternalId?.trim() || "",
    };

    onSearch(searchCriteria);
    form.reset();
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      centered
      withCloseButton={false}
      size="md"
      classNames={{
        content: styles.modalContent,
        body: styles.modalBody,
      }}
    >
      <div className={styles.modalHeader}>
        <div className={styles.featuredIcon}>
          <FontAwesomeIcon icon={faSearch} />
        </div>
        <button
          type="button"
          className={styles.closeButton}
          onClick={handleClose}
          aria-label="Close search modal"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>

      <h2 className={styles.modalTitle}>Search for Referrals</h2>

      <form
        onSubmit={form.onSubmit(handleSubmit)}
        className={styles.formContainer}
      >
        <div className={styles.fieldRow}>
          <TextInput
            label="First Name"
            placeholder="Enter first name"
            {...form.getInputProps("patientFirstName")}
            classNames={{
              root: styles.fieldRoot,
              label: styles.fieldLabel,
              input: styles.fieldInput,
            }}
          />
          <TextInput
            label="Last Name"
            placeholder="Enter last name"
            {...form.getInputProps("patientLastName")}
            classNames={{
              root: styles.fieldRoot,
              label: styles.fieldLabel,
              input: styles.fieldInput,
            }}
          />
        </div>

        <div className={styles.dateField}>
          <TextInput
            type="date"
            label="Date of Birth"
            {...form.getInputProps("patientDateOfBirth")}
            classNames={{
              root: styles.fieldRoot,
              label: styles.fieldLabel,
              input: styles.fieldInput,
            }}
          />
        </div>

        <div className={styles.dateField}>
          <TextInput
            label="External ID"
            placeholder="Enter external identifier"
            {...form.getInputProps("patientExternalId")}
            classNames={{
              root: styles.fieldRoot,
              label: styles.fieldLabel,
              input: styles.fieldInput,
            }}
          />
        </div>

        <div className={styles.modalActions}>
          <button type="submit" className={styles.continueButton}>
            Search
          </button>
        </div>
      </form>
    </Modal>
  );
};
